import { 
  Users,
  Brain,
  Clock,
  TrendingUp,
  Shield,
  Award,
  BarChart3,
  Zap,
  Target,
  Calendar,
  FileText,
  Briefcase,
  Heart,
  Globe,
  Lock,
  Database,
  Network,
  CheckCircle,
  Activity,
  Rocket,
  UserCheck,
  DollarSign,
  Settings,
  Smartphone,
  GraduationCap,
  MessageSquare,
  Search,
  Share2,
  Layers,
  PieChart,
  Workflow,
  Sparkles,
  LineChart,
  Headphones,
  Building2,
  ArrowUpRight,
  Gift,
  RefreshCw,
  FileSearch,
  UserPlus,
  Compass
} from 'lucide-react';
import type { ProductFeatureConfig } from './types';

export const completeHrManagementPlatformConfig: ProductFeatureConfig = {
  keyBenefits: [
    {
      icon: Brain,
      title: "Skills-First Talent Strategy",
      description: "Leverage AI-powered skills intelligence to understand, develop, and act on workforce skills in real-time. Match employees to opportunities based on verified competencies, not just job titles.",
      metrics: "Skills Cloud AI",
      color: "from-purple-500 to-indigo-600"
    },
    {
      icon: Database,
      title: "Unified HR Data Core",
      description: "Single source of truth unifying employee records, payroll, benefits, time, and performance data in one secure platform. Eliminate silos and enable real-time workforce insights.",
      metrics: "360° employee view",
      color: "from-blue-500 to-cyan-600"
    },
    {
      icon: Smartphone,
      title: "Frictionless Employee Self-Service",
      description: "Mobile-first experience empowering employees to access payslips, book holidays, update details, submit expenses, and get HR support anytime, anywhere.",
      metrics: "24/7 mobile access",
      color: "from-teal-500 to-emerald-600"
    },
    {
      icon: Globe,
      title: "Global Compliance & Agility",
      description: "Manage UK HMRC requirements and local regulations with a cloud-based system that automatically adapts to changing legislation, tax codes, and business objectives.",
      metrics: "Auto-compliance",
      color: "from-indigo-500 to-purple-600"
    },
    {
      icon: DollarSign,
      title: "Effortless Automated Payroll",
      description: "HMRC-integrated payroll with Real Time Information (RTI) submissions, automatic P45/P60 generation, pension auto-enrolment, and statutory pay calculations.",
      metrics: "99.9% accuracy",
      color: "from-emerald-500 to-teal-600"
    },
    {
      icon: LineChart,
      title: "Intelligent Workforce Planning",
      description: "Model, forecast, and budget your workforce using real-time data analytics. Replace static spreadsheets with dynamic planning tools that adapt to business changes.",
      metrics: "Predictive planning",
      color: "from-orange-500 to-amber-600"
    },
    {
      icon: GraduationCap,
      title: "Interactive Learning Ecosystem",
      description: "Deliver personalised career guidance, skills development pathways, and upskilling opportunities from day one. Track certifications and measure L&D effectiveness.",
      metrics: "Career pathways",
      color: "from-violet-500 to-purple-600"
    },
    {
      icon: Users,
      title: "Total Workforce Visibility",
      description: "Manage permanent employees, contractors, and contingent workers in a single integrated view. Gain complete visibility across your entire extended workforce.",
      metrics: "Unified workforce",
      color: "from-blue-500 to-indigo-600"
    },
    {
      icon: MessageSquare,
      title: "Pulse & Sentiment Analysis",
      description: "Keep a pulse on employee feelings with AI-powered surveys that ask the right questions at the right time. Detect engagement trends and take proactive action.",
      metrics: "Real-time sentiment",
      color: "from-pink-500 to-rose-600"
    },
    {
      icon: Award,
      title: "Performance & Goal Management",
      description: "360-degree feedback, OKR tracking, continuous performance conversations, and career path planning driving engagement and retention across your organisation.",
      metrics: "45% engagement boost",
      color: "from-amber-500 to-orange-600"
    },
    {
      icon: Search,
      title: "AI-Powered Recruitment & ATS",
      description: "Intelligent applicant tracking with AI CV screening, candidate matching, automated interview scheduling, and seamless onboarding workflow automation.",
      metrics: "50% faster hiring",
      color: "from-cyan-500 to-blue-600"
    },
    {
      icon: Heart,
      title: "Benefits & Wellbeing Portal",
      description: "Comprehensive benefits administration with flexible benefits selection, salary sacrifice schemes, and a curated ecosystem of health and wellbeing partners.",
      metrics: "Holistic wellness",
      color: "from-rose-500 to-pink-600"
    }
  ],
  
  advancedCapabilities: [
    {
      icon: Sparkles,
      title: "Illuminate™ Next-Gen AI Engine",
      description: "Power HR processes with generative AI that automates routine tasks, generates insights, drafts communications, and frees up your team for strategic work.",
      metrics: "Gen AI automation",
      color: "from-purple-500 to-pink-600"
    },
    {
      icon: BarChart3,
      title: "Augmented People Analytics",
      description: "Transform vast employee datasets into actionable insights. Uncover critical workforce opportunities, predict trends, and drive data-informed HR decisions.",
      metrics: "AI-powered insights",
      color: "from-blue-500 to-indigo-600"
    },
    {
      icon: Target,
      title: "AI-Driven Demand Forecasting",
      description: "Optimise staffing levels by matching worker preferences with business demand through predictive scheduling algorithms that balance efficiency and employee satisfaction.",
      metrics: "Predictive scheduling",
      color: "from-emerald-500 to-teal-600"
    },
    {
      icon: Headphones,
      title: "Conversational AI Recruitment",
      description: "Enhance candidate experiences and reduce drop-off rates with AI-driven interview scheduling, chatbot engagement, and intelligent candidate communication.",
      metrics: "AI recruitment",
      color: "from-orange-500 to-amber-600"
    },
    {
      icon: Settings,
      title: "Low-Code App Development",
      description: "Use visual no-code and low-code tools to build custom HR applications, workflows, and automations directly on the platform without developer resources.",
      metrics: "Extensible platform",
      color: "from-indigo-500 to-violet-600"
    },
    {
      icon: Briefcase,
      title: "Proactive Manager Insights Hub",
      description: "Provide managers with real-time career activity dashboards, team performance analytics, coaching tools, and actionable recommendations for their direct reports.",
      metrics: "Manager empowerment",
      color: "from-cyan-500 to-blue-600"
    },
    {
      icon: ArrowUpRight,
      title: "Skills-Based Internal Mobility",
      description: "Remove barriers to internal hiring by matching employees to new roles based on verified skills, interests, and development goals rather than job titles alone.",
      metrics: "Internal talent market",
      color: "from-green-500 to-emerald-600"
    },
    {
      icon: Layers,
      title: "Object-Oriented Data Architecture",
      description: "Utilise a flexible architecture supporting personas, complex org structures, matrix management, and multi-entity configurations for enterprise scalability.",
      metrics: "Enterprise flexibility",
      color: "from-violet-500 to-purple-600"
    },
    {
      icon: PieChart,
      title: "Diversity & Inclusion Analytics",
      description: "Comprehensive D&I metrics, pay equity analysis, representation reporting, and AI-powered bias detection in hiring, promotions, and compensation decisions.",
      metrics: "Pay equity insights",
      color: "from-pink-500 to-rose-600"
    },
    {
      icon: RefreshCw,
      title: "Continuous Listening Platform",
      description: "Always-on employee feedback with pulse surveys, lifecycle surveys, exit interviews, and sentiment analysis providing continuous workforce intelligence.",
      metrics: "Voice of employee",
      color: "from-teal-500 to-cyan-600"
    },
    {
      icon: Workflow,
      title: "Succession & Talent Pipeline",
      description: "Identify high-potential employees, plan succession for key roles, maintain talent pipelines, and ensure business continuity with future-ready leadership.",
      metrics: "Leadership pipeline",
      color: "from-amber-500 to-orange-600"
    },
    {
      icon: Share2,
      title: "500+ Integration Ecosystem",
      description: "Seamless connections with accounting (Xero, Sage, QuickBooks), job boards (Indeed, LinkedIn), SSO providers (Azure AD, Okta), and productivity tools.",
      metrics: "500+ integrations",
      color: "from-blue-500 to-cyan-600"
    }
  ],
  
  securityPerformance: [
    {
      icon: Target,
      title: 'SOC 2 & ISO 27001 Compliance',
      description: 'Full compliance with international security standards including SOC 2 Type II, ISO 27001, and UK Cyber Essentials Plus certification.',
      metrics: 'Complete regulatory compliance',
      color: 'from-indigo-500 to-purple-600'
    },
    {
      icon: Shield,
      title: 'UK GDPR Data Protection',
      description: 'Comprehensive GDPR compliance with DSAR automation, consent management, right to erasure support, and data portability.',
      metrics: 'Full GDPR compliance',
      color: 'from-green-500 to-emerald-600'
    },
    {
      icon: Rocket,
      title: 'Enterprise Cloud Infrastructure',
      description: 'UK-based AWS data centres with auto-scaling, global CDN, and 99.99% uptime SLA for reliable HR operations.',
      metrics: '99.99% uptime guaranteed',
      color: 'from-blue-500 to-indigo-600'
    },
    {
      icon: Lock,
      title: 'End-to-End Encryption',
      description: 'Military-grade AES-256 encryption for data at rest and TLS 1.3 for data in transit protecting sensitive employee information.',
      metrics: 'Military-grade security',
      color: 'from-red-500 to-pink-600'
    },
    {
      icon: CheckCircle,
      title: 'Multi-Factor Authentication',
      description: 'Enterprise SSO integration with Microsoft Azure AD, Google Workspace, and Okta, plus biometric and hardware token support.',
      metrics: 'Zero-trust access control',
      color: 'from-purple-500 to-indigo-600'
    },
    {
      icon: Activity,
      title: 'Comprehensive Audit Trails',
      description: 'Complete logging of all user actions, data access, and changes with tamper-proof audit trails for compliance and investigations.',
      metrics: 'Complete audit visibility',
      color: 'from-yellow-500 to-amber-600'
    },
    {
      icon: Database,
      title: 'Data Backup & Disaster Recovery',
      description: 'Automated hourly backups with point-in-time recovery, geo-redundant storage across UK regions, and tested disaster recovery.',
      metrics: 'Zero data loss guarantee',
      color: 'from-emerald-500 to-green-600'
    },
    {
      icon: Network,
      title: 'Network Security & DDoS Protection',
      description: 'Advanced firewall protection, intrusion detection, DDoS mitigation, and real-time threat monitoring for platform security.',
      metrics: 'Real-time threat protection',
      color: 'from-orange-500 to-red-600'
    },
    {
      icon: UserCheck,
      title: 'Role-Based Access Control',
      description: 'Granular permissions with role-based access, IP whitelisting, session management, and access review workflows.',
      metrics: 'Granular permissions',
      color: 'from-cyan-500 to-blue-600'
    }
  ]
};
